// The chart picture choice of the MAP section (ui-map-interface.md §13,
// R1731; `d_chartPick` of card 20 of mockup round 081).
//
// Three preview tiles in one row (`previewTiles` of the choice kit,
// ui-map-interface.md §18, R2011), one for each reading of the sea-chart
// furniture: a small picture of the reading over its word, and the chosen
// tile lit. Each tile is a button with the act and the `data-id` of the
// chart row (`rows.ts` `ACT.chart`), so `applyPress` and both hosts read a
// press on a tile as they read a press on a pill.
//
// A picture is decoration: the SVG is `aria-hidden`, and the word under it
// names the box. The picture holds no word of a language. The HISTORICAL
// picture letters two soundings in the Fell face of the chart, and the
// MODERN picture writes a depth and a scale figure in the mono face.
// `options.css` holds the inks of the pictures, each one a token.

import { CHART_READINGS, type ChartSetting } from '../settings';
import { previewTiles } from '../choice/choice';

/** The ground every picture shares: the hex water, a claim border, the
 *  own hold and a fleet. The view box is 120 × 50. */
const BASE = '<rect class="cp-bg" width="120" height="50" rx="3"/>'
  + '<path class="cp-hx" d="M18 6l9 5v10l-9 5-9-5V11zM36 6l9 5v10l-9 5-9-5V11zM27 21l9 5v10l-9 5-9-5V26zM45 21l9 5v10l-9 5-9-5V26z"/>'
  + '<path class="cp-bd" d="M54 26l9-5 9 5v10l9 5"/><path class="cp-own" d="M27 22l7 4v8l-7 4-7-4v-8z"/>'
  + '<path class="cp-fl" d="M78 30l7 3-7 3 2-3z"/>';

/** What each reading adds to the ground. HISTORICAL: a compass rose with
 *  rhumb lines and two soundings in the Fell face. MODERN: a scale bar,
 *  the ticks of the margin, a depth contour and two mono figures. */
const PICTURES: Record<ChartSetting, string> = {
  off: BASE,
  historical: BASE + '<g class="cp-ink"><circle cx="102" cy="20" r="8"/><path d="M102 9v22M91 20h22"/>'
    + '<path class="cp-fill" d="M102 11l2 7-2 2-2-2zM111 20l-7 2-2-2 2-2z"/>'
    + '<path class="cp-rh" d="M102 20L60 8M102 20L88 48"/></g>'
    + '<text class="cp-fell" x="62" y="17" font-size="8">24</text>'
    + '<text class="cp-fell" x="56" y="43" font-size="8">36</text>',
  modern: BASE + '<g class="cp-mod"><path d="M0 0h120M10 0v3M30 0v3M50 0v3M70 0v3M90 0v3M110 0v3M0 10h3M0 25h3M0 40h3"/>'
    + '<path class="cp-iso" d="M60 49c8-10 22-12 30-22s20-8 30-12"/><path d="M92 44h20M92 42v4M112 42v4"/></g>'
    + '<text class="cp-mono" x="88" y="22" font-size="6.5">120 m</text>'
    + '<text class="cp-mono" x="94" y="40" font-size="6.5">500</text>',
};

/** The picture of one reading, as the SVG a box holds. */
export function chartPicture(reading: ChartSetting): string {
  return '<svg viewBox="0 0 120 50" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">'
    + `${PICTURES[reading]}</svg>`;
}

/**
 * The chart choice: one tile for each reading, in the order of
 * `CHART_READINGS`. The chosen tile wears the accent, a bar along the foot
 * of its word and a filled radio mark, so it reads by shape as well as by
 * colour. `words` names each tile, `label` the group.
 */
export function chartPick(chosen: ChartSetting, act: string,
  words: Readonly<Record<ChartSetting, string>>, label = ''): string {
  return previewTiles(CHART_READINGS.map((c) => ({ id: c, label: words[c], picture: chartPicture(c) })),
    chosen, act, { label, cols: CHART_READINGS.length });
}
